import { useState } from 'react';
import JSZip from 'jszip';
import { useAuth } from './AuthContext';
import { FileTreeView, countNodes } from './FileTree';

const DUMMY_ZIPS = [
  {
    id: 1,
    label: 'Frontend — React',
    filename: 'frontend-react.zip',
    size: '2.4 MB',
    status: 'ready',
    tree: {
      name: 'frontend-react', type: 'folder',
      children: [
        {
          name: 'src', type: 'folder',
          children: [
            {
              name: 'components', type: 'folder',
              children: [
                { name: 'AppHeader.jsx',    type: 'file' },
                { name: 'AppSidebar.jsx',   type: 'file' },
                { name: 'DataGrid.jsx',     type: 'file' },
                { name: 'FormBuilder.jsx',  type: 'file' },
                { name: 'Modal.jsx',        type: 'file' },
              ],
            },
            {
              name: 'pages', type: 'folder',
              children: [
                { name: 'DashboardPage.jsx',  type: 'file' },
                { name: 'LoginPage.jsx',      type: 'file' },
                { name: 'SettingsPage.jsx',   type: 'file' },
                { name: 'UserManagePage.jsx', type: 'file' },
              ],
            },
            {
              name: 'store', type: 'folder',
              children: [
                { name: 'index.js',      type: 'file' },
                { name: 'authSlice.js',  type: 'file' },
                { name: 'dataSlice.js',  type: 'file' },
              ],
            },
            { name: 'App.jsx',    type: 'file' },
            { name: 'index.js',   type: 'file' },
            { name: 'routes.js',  type: 'file' },
          ],
        },
        {
          name: 'public', type: 'folder',
          children: [
            { name: 'index.html',    type: 'file' },
            { name: 'favicon.ico',   type: 'file' },
            { name: 'manifest.json', type: 'file' },
          ],
        },
        { name: 'package.json', type: 'file' },
        { name: 'README.md',    type: 'file' },
        { name: '.gitignore',   type: 'file' },
      ],
    },
  },
  {
    id: 2,
    label: 'Backend — .NET API',
    filename: 'backend-dotnet.zip',
    size: '1.8 MB',
    status: 'ready',
    tree: {
      name: 'backend-dotnet', type: 'folder',
      children: [
        {
          name: 'Controllers', type: 'folder',
          children: [
            { name: 'AuthController.cs',     type: 'file' },
            { name: 'ProjectController.cs',  type: 'file' },
            { name: 'ResourceController.cs', type: 'file' },
            { name: 'UserController.cs',     type: 'file' },
          ],
        },
        {
          name: 'Services', type: 'folder',
          children: [
            { name: 'AuthService.cs',     type: 'file' },
            { name: 'ProjectService.cs',  type: 'file' },
            { name: 'ResourceService.cs', type: 'file' },
          ],
        },
        {
          name: 'Models', type: 'folder',
          children: [
            { name: 'Project.cs',   type: 'file' },
            { name: 'Resource.cs',  type: 'file' },
            { name: 'User.cs',      type: 'file' },
          ],
        },
        {
          name: 'Data', type: 'folder',
          children: [
            { name: 'AppDbContext.cs',   type: 'file' },
            { name: 'Migrations', type: 'folder',
              children: [
                { name: '001_InitialCreate.cs', type: 'file' },
                { name: '002_AddResources.cs',  type: 'file' },
              ],
            },
          ],
        },
        { name: 'Program.cs',          type: 'file' },
        { name: 'appsettings.json',    type: 'file' },
        { name: 'backend-dotnet.csproj', type: 'file' },
      ],
    },
  },
  {
    id: 3,
    label: 'Database Scripts',
    filename: 'database-scripts.zip',
    size: '340 KB',
    status: 'ready',
    tree: {
      name: 'database-scripts', type: 'folder',
      children: [
        {
          name: 'schema', type: 'folder',
          children: [
            { name: '001_create_tables.sql',    type: 'file' },
            { name: '002_create_indexes.sql',   type: 'file' },
            { name: '003_create_views.sql',     type: 'file' },
          ],
        },
        {
          name: 'seed', type: 'folder',
          children: [
            { name: 'seed_codelists.sql', type: 'file' },
            { name: 'seed_users.sql',     type: 'file' },
          ],
        },
        {
          name: 'stored-procedures', type: 'folder',
          children: [
            { name: 'sp_GetProjectTree.sql',  type: 'file' },
            { name: 'sp_AssignUserRole.sql',  type: 'file' },
          ],
        },
        { name: 'README.md', type: 'file' },
      ],
    },
  },
  {
    id: 4,
    label: 'Config & Deployment',
    filename: 'config-deploy.zip',
    size: '120 KB',
    status: 'ready',
    tree: {
      name: 'config-deploy', type: 'folder',
      children: [
        {
          name: 'docker', type: 'folder',
          children: [
            { name: 'Dockerfile.frontend',  type: 'file' },
            { name: 'Dockerfile.backend',   type: 'file' },
            { name: 'docker-compose.yml',   type: 'file' },
          ],
        },
        {
          name: 'nginx', type: 'folder',
          children: [
            { name: 'nginx.conf',        type: 'file' },
            { name: 'ssl.conf',          type: 'file' },
          ],
        },
        {
          name: 'env', type: 'folder',
          children: [
            { name: '.env.development',  type: 'file' },
            { name: '.env.production',   type: 'file' },
          ],
        },
        { name: 'deploy.sh',   type: 'file' },
        { name: 'README.md',   type: 'file' },
      ],
    },
  },
];

function buildZip(zip, nodes, path = '') {
  for (const node of (nodes ?? [])) {
    const name = node.name ?? node.fileName ?? 'unknown';
    const fullPath = path ? `${path}/${name}` : name;
    const isFolder = node.nodeType === 'Folder' || node.type === 'folder' || node.isDirectory === true;
    const children = node.children ?? node.files ?? node.items ?? [];
    if (isFolder) {
      zip.folder(fullPath);
      buildZip(zip, children, fullPath);
    } else {
      zip.file(fullPath, '');
    }
  }
}

export default function DownloadPage() {
  const { project } = useAuth();
  const [selectedId,  setSelectedId]  = useState(DUMMY_ZIPS[0].id);
  const [downloading, setDownloading] = useState(null);
  const [pkgSearch,   setPkgSearch]   = useState('');

  const filteredZips = pkgSearch.trim()
    ? DUMMY_ZIPS.filter(z =>
        z.label.toLowerCase().includes(pkgSearch.toLowerCase()) ||
        z.filename.toLowerCase().includes(pkgSearch.toLowerCase())
      )
    : DUMMY_ZIPS;

  const selected = DUMMY_ZIPS.find(z => z.id === selectedId);
  const totalItems = selected ? countNodes(selected.tree) : 0;

  async function handleDownload(zipEntry) {
    setDownloading(zipEntry.id);
    try {
      const zip = new JSZip();
      buildZip(zip, zipEntry.tree.children ?? [], zipEntry.tree.name);

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipEntry.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to generate ZIP. Please try again.');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div style={S.page}>

      {/* ── Left: ZIP list ── */}
      <div style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <span style={S.sidebarTitle}>Output Packages</span>
          <span style={S.sideBadge}>{filteredZips.length}</span>
        </div>

        {/* Search */}
        <div style={S.searchWrap}>
          <span style={S.searchIcon}>🔍</span>
          <input
            style={S.searchInput}
            type="text"
            autoComplete="off"
            placeholder="Search packages…"
            value={pkgSearch}
            onChange={e => setPkgSearch(e.target.value)}
          />
          {pkgSearch && (
            <button style={S.searchClear} onClick={() => setPkgSearch('')}>✕</button>
          )}
        </div>

        <div style={S.sidebarList}>
          {filteredZips.length === 0 && (
            <p style={S.noResults}>No packages match "{pkgSearch}"</p>
          )}
          {filteredZips.map(z => {
            const isActive  = selectedId === z.id;
            const isLoading = downloading === z.id;
            return (
              <button
                key={z.id}
                style={{ ...S.zipItem, ...(isActive ? S.zipItemActive : {}) }}
                onClick={() => setSelectedId(z.id)}
              >
                <span style={S.zipIcon}>🗜️</span>
                <div style={S.zipInfo}>
                  <span style={S.zipName}>{z.label}</span>
                  <span style={S.zipMeta}>{z.filename} · {z.size}</span>
                </div>
                <button
                  style={{ ...S.inlineDownloadBtn, ...(isLoading ? S.inlineDownloadBtnBusy : {}) }}
                  onClick={e => { e.stopPropagation(); handleDownload(z); }}
                  disabled={isLoading}
                  title={`Download ${z.filename}`}
                >
                  {isLoading ? '…' : '⬇'}
                </button>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right: tree preview + download ── */}
      <div style={S.main}>
        <div style={S.mainHeader}>
          <div style={S.mainHeaderLeft}>
            <span style={S.mainIcon}>🗜️</span>
            <div>
              <div style={S.mainTitle}>{selected?.label ?? '—'}</div>
              <div style={S.mainPath}>
                <span style={S.pathMono}>
                  $Output
                  <span style={{ opacity: 0.45 }}>\</span>
                  {project.id}
                  <span style={{ opacity: 0.45 }}>\</span>
                  {selected?.filename ?? '—'}
                </span>
                <span style={S.itemBadge}>{totalItems} items</span>
                <span style={S.aiTag}>✨ AI Converted</span>
                <span style={S.sizeBadge}>{selected?.size}</span>
              </div>
            </div>
          </div>

          <button
            style={{ ...S.downloadBtn, ...(downloading === selectedId ? S.downloadBtnDisabled : {}) }}
            onClick={() => handleDownload(selected)}
            disabled={downloading === selectedId}
          >
            {downloading === selectedId ? (
              <>
                <span style={S.btnSpinner} />
                Generating…
              </>
            ) : (
              <>
                <span style={{ fontSize: 14 }}>⬇️</span>
                Download ZIP
              </>
            )}
          </button>
        </div>

        {/* Info banner */}
        <div style={S.infoBanner}>
          <span style={{ fontSize: 15 }}>🤖</span>
          <span style={S.infoText}>
            Preview of AI-converted output packages for project <strong>{project.name ?? `#${project.id}`}</strong>.
            The actual ZIPs will be served by the server once AI processing is complete.
          </span>
        </div>

        {/* Tree */}
        <div style={S.treeArea}>
          {selected && (
            <div style={S.treeCard}>
              <FileTreeView node={selected.tree} />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

const S = {
  page: {
    display: 'flex', flexDirection: 'row',
    flex: 1, minHeight: 0, height: '100%',
    overflow: 'hidden',
  },

  /* ── Sidebar ── */
  sidebar: {
    width: 260, minWidth: 220, maxWidth: 300,
    flexShrink: 0,
    display: 'flex', flexDirection: 'column',
    borderRight: '1px solid var(--border)',
    background: 'var(--bg-card)',
    overflow: 'hidden',
  },
  sidebarHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px 10px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
  },
  sidebarTitle: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)' },
  sideBadge: {
    background: 'var(--primary)', color: '#fff',
    borderRadius: 10, fontSize: 10, fontWeight: 700,
    padding: '1px 7px',
  },
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0,
  },
  searchIcon: {
    position: 'absolute', left: 18, fontSize: 11,
    color: 'var(--text-muted)', pointerEvents: 'none',
  },
  searchInput: {
    width: '100%', padding: '6px 28px 6px 24px',
    border: '1.5px solid var(--border)', borderRadius: 6,
    background: 'var(--bg-page)', color: 'var(--text-primary)',
    fontSize: 12, outline: 'none', fontFamily: 'var(--font-family)',
    boxSizing: 'border-box',
  },
  searchClear: {
    position: 'absolute', right: 16,
    background: 'none', border: 'none',
    cursor: 'pointer', color: 'var(--text-muted)',
    fontSize: 11, padding: 2, lineHeight: 1,
  },
  noResults: {
    fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic',
    padding: '12px 8px', margin: 0, textAlign: 'center',
  },
  sidebarList: { flex: 1, overflowY: 'auto', padding: '6px 8px' },

  zipItem: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px', borderRadius: 7,
    border: 'none', background: 'transparent',
    cursor: 'pointer', textAlign: 'left',
    transition: 'background 0.12s',
    marginBottom: 2,
  },
  zipItemActive: {
    background: 'var(--info-bg, rgba(21,101,192,0.1))',
    border: '1px solid rgba(21,101,192,0.2)',
  },
  zipIcon: { fontSize: 15, flexShrink: 0 },
  zipInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 },
  zipName: { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  zipMeta: { fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  inlineDownloadBtn: {
    flexShrink: 0,
    background: 'var(--primary)',
    border: 'none', borderRadius: 5,
    color: '#fff', fontSize: 11, fontWeight: 700,
    width: 24, height: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  inlineDownloadBtnBusy: { opacity: 0.5, cursor: 'not-allowed' },

  /* ── Main ── */
  main: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  mainHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-secondary, rgba(0,0,0,0.02))',
    flexShrink: 0, gap: 12,
  },
  mainHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 },
  mainIcon: { fontSize: 20, flexShrink: 0 },
  mainTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 },
  mainPath: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' },
  pathMono: { fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--text-muted)' },
  itemBadge: {
    background: 'var(--info-bg, rgba(21,101,192,0.1))',
    border: '1px solid rgba(21,101,192,0.2)',
    borderRadius: 10, color: 'var(--primary)',
    fontSize: 10, fontWeight: 700, padding: '1px 8px',
  },
  aiTag: {
    background: 'rgba(124,58,237,0.1)',
    border: '1px solid rgba(124,58,237,0.25)',
    borderRadius: 10, color: '#7c3aed',
    fontSize: 10, fontWeight: 700, padding: '1px 8px',
  },
  sizeBadge: {
    background: 'rgba(16,185,129,0.1)',
    border: '1px solid rgba(16,185,129,0.25)',
    borderRadius: 10, color: '#059669',
    fontSize: 10, fontWeight: 700, padding: '1px 8px',
  },

  infoBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    margin: '16px 20px 0',
    background: 'rgba(21,101,192,0.06)',
    border: '1px solid rgba(21,101,192,0.18)',
    borderRadius: 8, padding: '10px 14px',
    flexShrink: 0,
  },
  infoText: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 },

  downloadBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'var(--primary)',
    border: 'none', borderRadius: 8,
    color: '#fff', fontSize: 13, fontWeight: 600,
    padding: '8px 16px', cursor: 'pointer',
    flexShrink: 0, transition: 'opacity 0.15s',
    whiteSpace: 'nowrap',
  },
  downloadBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  btnSpinner: {
    display: 'inline-block',
    width: 12, height: 12,
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },

  treeArea: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  treeCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8, padding: '14px 16px',
    boxShadow: 'var(--shadow-xs)',
  },
};
